/**
 * FixWorkspace — the "Fix issues" steps at a readable size.
 *
 *   1. Issues  — every selected flag, by section, each with its own tick box,
 *                and one chip per flag type to tick or untick all of that type.
 *   2. Sources — which documents to search (none picked = the whole library).
 *   3. Confirm — what will be fixed, from where, and what to expect.
 *
 * After Confirm the window stays open and shows the pass: each issue's status,
 * the agent's live action under the section being fixed, and at the end what
 * happened to every issue, with the changes ready to review.
 *
 * Pure view: it renders in the Fix window (an Office dialog) and, where dialogs
 * are unavailable, over the task pane. Start and Cancel go out as callbacks.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ResolveIssueDoc } from "../../api/client";
import { brand } from "../../config/brand";
import {
  Button,
  Eyebrow,
  LinkButton,
  Notice,
  type PillStatus,
  SearchField,
  StatusPill,
  Stepper,
} from "../../ds/components";
import {
  EMPTY_FIX_DRAFT,
  FIX_STATUS_LABEL,
  type FixDraft,
  type FixItemStatus,
  type FixProgress,
  type FixSetup,
  fixDurationLabel,
  fixSummaryLine,
  fixTally,
  issueTypes,
} from "./fixProtocol";
import { SizeToggle, type ViewPlacement } from "./SizeToggle";

export interface FixWorkspaceProps {
  setup: FixSetup | null;
  busy?: boolean;
  onStart: (issueIds: string[], documentScope: ResolveIssueDoc[]) => void;
  onCancel: () => void;
  /** Where the writer left off (after Expand / Minimise), and each change to it. */
  draft?: FixDraft;
  onDraftChange?: (draft: FixDraft) => void;
  placement?: ViewPlacement;
  onResize?: () => void;
  /** Set once the pass has started: the window shows it instead of the steps. */
  progress?: FixProgress | null;
  onPause?: () => void;
  onResume?: () => void;
  onStop?: () => void;
  /** Open the changes the pass proposed. */
  onReview?: () => void;
  /** Close the window; a pass still running carries on in the pane. */
  onClose?: () => void;
}

const LARGE_BATCH = 50;
const STEPS = ["Issues", "Sources", "Confirm"] as const;

/** A flag's status in the pass, as a design-system pill. */
const STATUS_PILL: Record<FixItemStatus, PillStatus> = {
  working: "drafting",
  ready: "done",
  approved: "done",
  rejected: "queued",
  not_in_sources: "paused",
  not_placed: "failed",
  failed: "failed",
  stopped: "queued",
  no_change: "queued",
};

// Layout only; colour, type and controls come from the design system
// (ds/asthra.css tokens and ds/components).
const useStyles = makeStyles({
  root: {
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--surface)",
    color: "var(--ink)",
    fontFamily: "var(--font-sans)",
    fontSize: "13px",
  },
  top: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "14px 20px 12px",
    borderBottom: "1px solid var(--line)",
  },
  topRow: { display: "flex", alignItems: "center", gap: "8px", minHeight: "28px" },
  title: { fontSize: "17px", lineHeight: "24px", fontWeight: 600, margin: 0 },
  lede: { fontSize: "13px", color: "var(--ink-muted)", lineHeight: 1.5, margin: 0, maxWidth: "640px" },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "14px 20px",
    backgroundColor: "var(--surface-sunken)",
  },
  toolbar: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "12px" },
  count: { fontSize: "12px", color: "var(--ink-muted)" },
  spacer: { flex: 1 },
  search: { flex: 1, minWidth: "180px" },
  chips: { display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "14px" },
  chip: {
    height: "26px",
    padding: "0 10px",
    borderRadius: "var(--radius-pill)",
    border: "1px solid var(--line-strong)",
    backgroundColor: "var(--surface)",
    color: "var(--ink)",
    font: "inherit",
    fontSize: "12px",
    cursor: "pointer",
  },
  chipOn: { border: "1px solid var(--brand)", backgroundColor: "var(--brand-soft)" },
  chipSome: { border: "1px dashed var(--brand)" },
  chipNum: { fontFamily: "var(--font-mono)", marginLeft: "6px", color: "var(--ink-muted)" },
  section: { marginBottom: "14px" },
  sectionHead: { display: "flex", alignItems: "baseline", gap: "8px", paddingBottom: "6px" },
  rows: {
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-md)",
    overflow: "hidden",
    backgroundColor: "var(--surface)",
  },
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "10px 12px",
    borderBottom: "1px solid var(--line)",
    cursor: "pointer",
    ":last-child": { borderBottom: 0 },
  },
  rowOff: { color: "var(--ink-muted)" },
  checkbox: { marginTop: "2px", accentColor: "var(--brand)", cursor: "pointer" },
  kind: { display: "block", marginBottom: "2px", fontSize: "12px", fontWeight: 600 },
  desc: { fontSize: "13px", lineHeight: 1.45, color: "inherit" },
  docName: { fontSize: "13px", fontWeight: 500 },
  summary: {
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-md)",
    overflow: "hidden",
    backgroundColor: "var(--surface)",
    marginBottom: "14px",
  },
  summaryRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "10px 12px",
    borderBottom: "1px solid var(--line)",
    ":last-child": { borderBottom: 0 },
  },
  summaryKey: { width: "96px", flexShrink: 0, fontSize: "12px", color: "var(--ink-muted)" },
  summaryVal: { flex: 1, fontWeight: 600 },
  notes: { margin: "0 0 12px", paddingLeft: "18px", color: "var(--ink)", lineHeight: 1.6 },
  empty: { color: "var(--ink-muted)", padding: "20px 0" },
  live: {
    marginTop: "4px",
    fontSize: "12px",
    color: "var(--ink-muted)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  progressText: { flex: 1, minWidth: 0 },
  footer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 20px",
    borderTop: "1px solid var(--line)",
    backgroundColor: "var(--surface)",
  },
});

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

export const FixWorkspace: React.FC<FixWorkspaceProps> = ({
  setup,
  busy = false,
  onStart,
  onCancel,
  draft = EMPTY_FIX_DRAFT,
  onDraftChange,
  placement = "pane",
  onResize,
  progress = null,
  onPause,
  onResume,
  onStop,
  onReview,
  onClose,
}) => {
  const styles = useStyles();
  const sections = setup?.sections ?? [];
  const allIds = useMemo(() => sections.flatMap((s) => s.issues.map((i) => i.id)), [sections]);
  const types = useMemo(() => issueTypes(sections), [sections]);
  const documents = useMemo(() => {
    const byId = new Map<string, { id: string; name: string }>();
    for (const d of setup?.documents ?? []) if (d.id) byId.set(d.id, d);
    return [...byId.values()];
  }, [setup]);

  const [step, setStep] = useState(draft.step);
  // Everything the writer picked in the pane starts ticked.
  const [unticked, setUnticked] = useState<Set<string>>(() => new Set(draft.unticked));
  const [picked, setPicked] = useState<Set<string>>(() => new Set(draft.picked));
  const [query, setQuery] = useState("");

  const onDraftRef = useRef(onDraftChange);
  onDraftRef.current = onDraftChange;
  useEffect(() => {
    onDraftRef.current?.({ step, unticked: [...unticked], picked: [...picked] });
  }, [step, unticked, picked]);

  const chosen = allIds.filter((id) => !unticked.has(id));
  const chosenSections = sections.filter((s) => s.issues.some((i) => !unticked.has(i.id)));

  const setTicked = (ids: string[], on: boolean) =>
    setUnticked((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.delete(id);
        else next.add(id);
      }
      return next;
    });

  const shownDocs = query.trim()
    ? documents.filter((d) => d.name.toLowerCase().includes(query.trim().toLowerCase()))
    : documents;
  const togglePicked = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const documentScope: ResolveIssueDoc[] = documents
    .filter((d) => picked.has(d.id))
    .map((d) => ({ doc_id: d.id, name: d.name }));

  const where = chosenSections.length === 1 ? `§${chosenSections[0].number}` : plural(chosenSections.length, "section");

  if (!setup) {
    return (
      <div className={styles.root}>
        <div className={styles.body}>
          <div className={styles.empty}>Loading the issues…</div>
        </div>
      </div>
    );
  }

  if (progress) {
    const inPass = sections
      .map((s) => ({ ...s, issues: s.issues.filter((i) => progress.status[i.id]) }))
      .filter((s) => s.issues.length > 0);
    const total = Object.keys(progress.status).length;
    const ready = Object.values(progress.status).filter((v) => v === "ready").length;
    const working = Object.values(progress.status).filter((v) => v === "working").length;
    const tally = fixTally(progress.status);
    const heading = progress.finished
      ? `${tally.fixed} fixed · ${tally.notFixed} not fixed · ${tally.pending} pending`
      : progress.paused
        ? `Paused · ${tally.pending} pending`
        : `Fixing ${plural(total, "issue")}`;
    return (
      <div className={styles.root}>
        <div className={styles.top}>
          <div className={styles.topRow}>
            <Eyebrow>Fix issues · {progress.finished ? "done" : progress.paused ? "paused" : "fixing"}</Eyebrow>
            <span className={styles.spacer} />
            <SizeToggle placement={placement} onToggle={onResize} />
          </div>
          <h1 className={styles.title}>{heading}</h1>
          {progress.error ? <Notice tone="danger">{progress.error}</Notice> : null}
          <p className={styles.lede} aria-live="polite">
            {progress.error
              ? null
              : progress.finished || progress.paused
                ? ready > 0
                  ? `${fixSummaryLine(progress.status)}. Fixed means a change is ready: nothing goes into Word until you approve it.`
                  : `${fixSummaryLine(progress.status)}.`
                : `${total - working} of ${total} done. The changes come to you together for review when the fix finishes. You can close this window: it keeps going, and the Issues tab shows its progress.`}
          </p>
        </div>
        <div className={styles.body}>
          {inPass.map((s) => (
            <div key={s.module_id} className={styles.section}>
              <div className={styles.sectionHead}>
                <Eyebrow as="h2">
                  {s.number} · {s.title}
                </Eyebrow>
              </div>
              <div className={styles.rows}>
                {s.issues.map((i) => {
                  const v = progress.status[i.id];
                  const live = v === "working" ? progress.live[s.module_id] : undefined;
                  return (
                    <div key={i.id} className={styles.row} data-testid="fix-progress-row">
                      <span className={styles.progressText}>
                        <span className={styles.kind}>{i.label}</span>
                        <span className={styles.desc}>{i.description || "(no description)"}</span>
                        {live ? (
                          <div className={styles.live} title={live} data-testid="fix-progress-live">
                            {live}
                          </div>
                        ) : null}
                      </span>
                      <span data-testid="fix-progress-status">
                        <StatusPill status={STATUS_PILL[v]}>{FIX_STATUS_LABEL[v]}</StatusPill>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className={styles.footer}>
          {!progress.finished && !progress.paused && onPause ? (
            <Button variant="ghost" onClick={onPause}>
              Pause
            </Button>
          ) : null}
          {progress.paused && onResume ? (
            <Button variant="ghost" onClick={onResume}>
              Resume
            </Button>
          ) : null}
          {/* Not while paused: nothing is running for a stop to reach (the
              server answers 404). The bar offers Discard for a paused pass. */}
          {!progress.finished && !progress.paused && onStop ? (
            <Button variant="ghost" onClick={onStop}>
              Stop
            </Button>
          ) : null}
          <span className={styles.spacer} />
          <Button variant={ready > 0 && onReview ? "ghost" : "secondary"} onClick={onClose ?? onCancel}>
            {progress.finished ? "Close" : "Run in background"}
          </Button>
          {/* All of a pass's changes are reviewed together, once it ends. */}
          {ready > 0 && onReview && (progress.finished || progress.paused) ? (
            <Button variant="primary" onClick={onReview}>
              Review {plural(ready, "change")}
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.top}>
        <div className={styles.topRow}>
          <Eyebrow>Fix issues · step {step + 1} of 3</Eyebrow>
          <span className={styles.spacer} />
          <SizeToggle placement={placement} onToggle={onResize} />
        </div>
        <Stepper steps={STEPS} current={step} />
        {step === 0 ? (
          <>
            <h1 className={styles.title}>Which issues should {brand.assistantName} fix?</h1>
            <p className={styles.lede}>
              {brand.assistantName} looks in your sources for what each issue needs and proposes a change for you to
              review. The rest of the section stays as it is.
            </p>
          </>
        ) : null}
        {step === 1 ? (
          <>
            <h1 className={styles.title}>Where should {brand.assistantName} look?</h1>
            <p className={styles.lede}>
              Pick the documents to search. If you pick none, it searches the whole library.
            </p>
          </>
        ) : null}
        {step === 2 ? <h1 className={styles.title}>Ready to fix {plural(chosen.length, "issue")}</h1> : null}
      </div>

      <div className={styles.body}>
        {step === 0 ? (
          <>
            <div className={styles.toolbar}>
              <span className={styles.count}>
                {chosen.length} of {allIds.length} selected
              </span>
              <span className={styles.spacer} />
              {chosen.length < allIds.length ? (
                <LinkButton onClick={() => setUnticked(new Set())}>Select all</LinkButton>
              ) : null}
              {chosen.length > 0 ? <LinkButton onClick={() => setUnticked(new Set(allIds))}>Clear</LinkButton> : null}
            </div>
            {types.length > 1 ? (
              // biome-ignore lint/a11y/useSemanticElements: a chip row, not a form fieldset
              <div className={styles.chips} role="group" aria-label="By flag type">
                {types.map((t) => {
                  const on = t.ids.filter((id) => !unticked.has(id)).length;
                  const all = on === t.ids.length;
                  return (
                    <button
                      type="button"
                      key={t.label}
                      className={mergeClasses(styles.chip, all && styles.chipOn, on > 0 && !all && styles.chipSome)}
                      aria-pressed={all}
                      title={all ? `Untick every ${t.label} issue` : `Tick every ${t.label} issue`}
                      onClick={() => setTicked(t.ids, !all)}
                    >
                      {t.label}
                      <span className={styles.chipNum}>{t.ids.length}</span>
                    </button>
                  );
                })}
              </div>
            ) : null}
            {sections.map((s) => (
              <div key={s.module_id} className={styles.section}>
                <div className={styles.sectionHead}>
                  <Eyebrow as="h2">
                    {s.number} · {s.title}
                  </Eyebrow>
                  <span className={styles.spacer} />
                  <span className={styles.count}>{plural(s.issues.length, "issue")}</span>
                </div>
                <div className={styles.rows}>
                  {s.issues.map((i) => {
                    const on = !unticked.has(i.id);
                    return (
                      <label key={i.id} className={mergeClasses(styles.row, !on && styles.rowOff)}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={on}
                          onChange={() => setTicked([i.id], !on)}
                        />
                        <span>
                          <span className={styles.kind}>{i.label}</span>
                          <span className={styles.desc}>{i.description || "(no description)"}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </>
        ) : null}

        {step === 1 ? (
          documents.length === 0 ? (
            <div className={styles.empty}>This run has no library documents listed. The whole library is searched.</div>
          ) : (
            <>
              <div className={styles.toolbar}>
                <div className={styles.search}>
                  <SearchField value={query} onChange={setQuery} placeholder="Find a document" />
                </div>
                <span className={styles.count}>
                  {picked.size === 0 ? "Whole library" : `${picked.size} of ${documents.length} picked`}
                </span>
                {picked.size > 0 ? (
                  <LinkButton onClick={() => setPicked(new Set())}>Clear</LinkButton>
                ) : (
                  <LinkButton onClick={() => setPicked(new Set(shownDocs.map((d) => d.id)))}>Select all</LinkButton>
                )}
              </div>
              <div className={styles.rows}>
                {shownDocs.map((d) => {
                  const on = picked.has(d.id);
                  return (
                    <label key={d.id} className={mergeClasses(styles.row, !on && styles.rowOff)}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={on}
                        onChange={() => togglePicked(d.id)}
                      />
                      <span className={styles.docName}>{d.name}</span>
                    </label>
                  );
                })}
              </div>
              {shownDocs.length === 0 ? <div className={styles.empty}>No document matches “{query}”.</div> : null}
            </>
          )
        ) : null}

        {step === 2 ? (
          <>
            <div className={styles.summary}>
              <div className={styles.summaryRow}>
                <span className={styles.summaryKey}>Issues</span>
                <span className={styles.summaryVal}>
                  {chosen.length} in {where}
                </span>
                <LinkButton onClick={() => setStep(0)}>Change</LinkButton>
              </div>
              <div className={styles.summaryRow}>
                <span className={styles.summaryKey}>Sources</span>
                <span className={styles.summaryVal}>
                  {documentScope.length === 0 ? "The whole library" : plural(documentScope.length, "picked document")}
                </span>
                <LinkButton onClick={() => setStep(1)}>Change</LinkButton>
              </div>
              <div className={styles.summaryRow}>
                <span className={styles.summaryKey}>Takes</span>
                <span className={styles.summaryVal}>{fixDurationLabel(chosenSections.length, chosen.length)}</span>
              </div>
            </div>
            <ul className={styles.notes}>
              <li>
                {brand.assistantName} proposes a fix for each issue. You approve each one, and only then does it go into
                Word.
              </li>
              <li>Missing facts can only be filled if your sources contain them. They are never made up.</li>
            </ul>
            {chosen.length > LARGE_BATCH ? (
              <Notice tone="warning">
                That is a large batch. It can take a while; you can untick some issues in step 1.
              </Notice>
            ) : null}
          </>
        ) : null}
      </div>

      <div className={styles.footer}>
        {step > 0 ? (
          <Button variant="ghost" onClick={() => setStep(step - 1)}>
            Back
          </Button>
        ) : null}
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <span className={styles.spacer} />
        {step < 2 ? (
          <Button variant="primary" disabled={chosen.length === 0} onClick={() => setStep(step + 1)}>
            Next: {step === 0 ? "sources" : "confirm"}
          </Button>
        ) : (
          <Button
            variant="primary"
            disabled={busy || chosen.length === 0}
            onClick={() => onStart(chosen, documentScope)}
          >
            {busy ? "Starting…" : `Fix ${plural(chosen.length, "issue")}`}
          </Button>
        )}
      </div>
    </div>
  );
};

export default FixWorkspace;
